import * as _ from 'lodash';
import { nanoid } from 'nanoid';
import type { VoidFunctionComponent } from 'react';

import { window } from '../../../../globals';
import { isOrgInFreeTier } from '../../../../helpers/subscriptions';
import { dropAt } from '../../../../utils';
import Button, { NavigationButton } from '../../../generic/Button';
import PaidFeatureGate from '../../../generic/Gates/PaidFeatureGate';
import { icons, IconText } from '../../../generic/Icon';
import Loader from '../../../generic/Loader';
import Header from '../../../shared/settings/SettingsPageHeader';
import Section from '../../../shared/settings/SettingsPageSection';
import useLanguageSettings from '../useLanguageSettings';
import type { NpmFetcherConfig, NpmRegistry, NpmRegistryAuth } from '../../../../../typings/auth';

import Registry from './Registry';

type EditableRegistryAuthProperty = keyof Omit<NpmRegistryAuth, 'alwaysAuth' | 'hasPassword' | 'hasToken'>;

const authProperties: EditableRegistryAuthProperty[] = ['email', 'username', 'password', 'token'];

const JavascriptSettings: VoidFunctionComponent = () => {
  const {
    settings,
    isSaving,
    onUpdateSettings,
    onSaveSettings,
    isSaveButtonDisabled,
    isLoading,
  } = useLanguageSettings(
    window.user?.organizationId as number,
    'npm',
  );

  // Note that our FE does not support the old legacy NPM config (NpmFetcherLegacyConfig), so this is a safe cast
  const registries = (settings as NpmFetcherConfig)?.registries || [];

  const addRegistry = () =>
    onUpdateSettings({
      registries: [
        ...registries,
        {
          _id: nanoid(),
          // The default url is public registry.
          url: 'https://registry.npmjs.org',
        },
      ],
    });

  const onRemoveRegistry = (index: number) =>
    onUpdateSettings({
      registries: dropAt(registries, index),
    });

  const onUpdateRegistry = <K extends keyof NpmRegistry>(index: number, member: K, value: NpmRegistry[K]) =>
    onUpdateSettings({
      registries: registries.map((registry, i) => {
        if (i !== index) {
          return registry;
        }

        const updatedRegistry: NpmRegistry = {
          ...registry,
          [member]: value || null, // We don't want to save empty strings.
        };

        if (member === 'ca') {
          updatedRegistry.hasCa = false;
        }

        return updatedRegistry;
      }),
    });

  const onUpdateRegistryAuth = <K extends keyof NpmRegistryAuth>(
    index: number,
    member: K,
    value: NpmRegistryAuth[K],
  ) => {
    const auth: NpmRegistryAuth = {
      ...(registries[index]?.auth || {}),
      [member]: value || null, // We don't want to save empty strings.
    };

    // Whether alwaysAuth is true depends on whether at least one auth value is set.
    auth.alwaysAuth = authProperties.some((key) => {
      if (key === 'password') {
        return (auth.password?.value.length || 0) > 0;
      }
      return (auth[key]?.trim() || '').length > 0;
    });

    if (member === 'password') {
      auth.hasPassword = false;
    } else if (member === 'token') {
      auth.hasToken = false;
    }

    onUpdateRegistry(index, 'auth', auth);
  };

  const setShowTokenField = (index: number, value: 'show-token-field' | 'hide-token-field') =>
    onUpdateSettings({
      registries: registries.map((registry, i): NpmRegistry => {
        if (i === index) {
          return {
            ...(_.omit(registry, 'auth') as Omit<NpmRegistry, 'auth'>),
            _showTokenField: value === 'show-token-field',
          };
        }
        return registry;
      }),
    });

  const isFreeOrg = isOrgInFreeTier();

  return (
    <PaidFeatureGate isRestricted={isFreeOrg} hidePill>
      <Header heading="NPM Settings">
        <Header.SaveButton
          onClick={onSaveSettings}
          isDisabled={isSaveButtonDisabled}
          isLoading={isSaving}
        />
      </Header>
      {isLoading
        ? <Loader />
        : (
          <Section>
            <Section.Left heading="Registry Authentications">
              <Section.Paragraph>
                For each registry, you may either provide an email/username/password or provide an Authentication Token.
              </Section.Paragraph>
              <NavigationButton
                type="secondary"
                size="medium"
                to="https://docs.npmjs.com/getting-started/working_with_tokens"
                openInNewTab
              >
                NPM documentation
              </NavigationButton>
            </Section.Left>
            <Section.Right>
              {registries.map((registry, index) => (
                <Registry
                  registry={registry}
                  onUpdateRegistryAuth={
                    <K extends keyof NpmRegistryAuth>(member: K, value: NpmRegistryAuth[K]) => {
                      onUpdateRegistryAuth(
                        index,
                        member,
                        value,
                      );
                    }
                  }
                  onUpdateRegistry={
                    <K extends keyof NpmRegistry>(member: K, value: NpmRegistry[K]) => {
                      onUpdateRegistry(
                        index,
                        member,
                        value,
                      );
                    }
                  }
                  onSetShowTokenField={(
                    value: 'show-token-field' | 'hide-token-field',
                  ) => setShowTokenField(index, value)}
                  onRemoveRegistry={() => onRemoveRegistry(index)}
                />
              ))}
              <Button
                type="secondary"
                size="medium"
                onClick={addRegistry}
              >
                <IconText icon={icons.plus} text="Add" />
              </Button>
            </Section.Right>
          </Section>)}
    </PaidFeatureGate>
  );
};

export default JavascriptSettings;
